/**
 * MenuItem — a single option row inside a design-system Menu (src/ui/ds).
 *
 * The atom of the menu family: one selectable row with an optional leading icon,
 * a label, an optional trailing hint, and a selection indicator. Token-driven.
 * Tones: default | danger (destructive actions — text.body.error, error.50 hover).
 * Selection: none (a plain command) | single (a checkmark + success text on the
 * selected row) | multi (a leading checkbox — the shared CheckboxVisual from the
 * DS Checkbox, success-green when checked).
 *
 * Genuinely interactive: real :hover tints the row and :focus-visible surfaces a
 * ring on the live element. `forceState` (used by the gallery) pins a state for
 * inspection. The row carries the correct menu ARIA role for its selection mode
 * (menuitem | menuitemradio | menuitemcheckbox); the Menu/MenuGroup it sits in
 * own the keyboard navigation and focus management.
 *
 *
 * PORTED from ploy-client src/ui/ds/MenuItem.tsx — the canonical design-system
 * component. Do not restyle here; upstream owns it. Portal-local changes:
 * dsTag inlined, viewer-only `whenToUse` dropped.
 *
 * Location: src/components/ds/MenuItem.tsx
 */
import { chakra } from "@chakra-ui/react";
import type { ReactNode } from "react";

/* dev-only DOM marker (inlined from ploy-client's ds/dsTag) — compiles away
   in production builds. */
const dsTag = (name: string): Record<string, string> =>
  import.meta.env.DEV ? { "data-ds": name } : {};

/** Minimal stand-in for the DS CheckboxVisual (the portal ds has no checkbox
    atom yet) — used only by `selection="multi"` rows. TOKEN/COMPONENT GAP. */
const CheckboxVisual = ({
  value,
  state,
}: {
  value?: "checked" | "unchecked";
  size?: "sm" | "md";
  state?: "default" | "disabled";
}) => {
  const checked = value === "checked";
  const disabled = state === "disabled";
  return (
  <chakra.span
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    w="16px"
    h="16px"
    flexShrink={0}
    borderRadius="component.sm"
    border="1px solid"
    borderColor={checked ? "success.600" : "border.strong"}
    bg={checked ? "success.600" : "bg.container"}
    color="neutral.0"
    opacity={disabled ? 0.4 : 1}
  >
      {checked && (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13 4.5L6 11.5L3 8.5" />
        </svg>
      )}
    </chakra.span>
  );
};

export type MenuItemTone = "default" | "danger";
export type MenuItemSelection = "none" | "single" | "multi";

export interface MenuItemProps {
  /** row label */
  children?: ReactNode;
  /** leading icon (inherits the row's text colour) */
  leadingIcon?: ReactNode;
  /** right-aligned hint — a shortcut, count, or secondary value */
  trailing?: ReactNode;
  /** secondary description line under the label (two-line row) */
  description?: ReactNode;
  /** opens a nested submenu — shows a trailing ▸ and sets aria-haspopup */
  hasSubmenu?: boolean;
  /**
   * selection model: none = command row; single = checkmark + success text on the
   * selected row; multi = a leading checkbox (success-green when checked).
   */
  selection?: MenuItemSelection;
  /** whether this row is selected/checked (single + multi) */
  selected?: boolean;
  /** destructive styling (text.body.error, error.50 hover) */
  tone?: MenuItemTone;
  isDisabled?: boolean;
  /** force a visual state for the gallery; runtime usually omits this */
  forceState?: "default" | "hover" | "focused" | "disabled";
  onClick?: (e: React.MouseEvent) => void;
}

/** check (Feather) — the single-select selected indicator. */
function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 4.5L6 11.5L3 8.5" />
    </svg>
  );
}

/** chevron-right (Feather) — the submenu-parent indicator. */
function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4l4 4-4 4" />
    </svg>
  );
}

export function MenuItem({
  children,
  leadingIcon,
  trailing,
  description,
  hasSubmenu = false,
  selection = "none",
  selected = false,
  tone = "default",
  isDisabled,
  forceState = "default",
  onClick,
}: MenuItemProps) {
  const disabled = Boolean(isDisabled) || forceState === "disabled";
  const forcedHover = forceState === "hover";
  const forcedFocus = forceState === "focused";
  const danger = tone === "danger";
  const single = selection === "single";
  const multi = selection === "multi";
  const showCheck = single && selected; // single-select right-aligned checkmark

  // Resting text colour: danger → error, single-select selected → success,
  // otherwise the standard body colour. Disabled overrides all.
  const textColor = disabled
    ? "text.disabled.default"
    : danger
      ? "text.body.error"
      : showCheck
        ? "text.body.success"
        : "text.body.primary";

  // Row highlight tiers: hover (subtle) < keyboard-focus / pressed (stronger).
  // Keyboard focus reads as a background highlight — the menu convention — not a
  // ring. Danger rows use the error tints.
  const hoverBg = danger ? "error.50" : "bg.hover";
  const strongBg = danger ? "error.100" : "bg.active";
  const baseBg = disabled ? "transparent" : forcedFocus ? strongBg : forcedHover ? hoverBg : "transparent";
  const hoverStyles = !disabled && !forcedHover && !forcedFocus ? { bg: hoverBg } : undefined;

  return (
    <chakra.button
      {...dsTag("MenuItem")}
      type="button"
      role={single ? "menuitemradio" : multi ? "menuitemcheckbox" : "menuitem"}
      aria-checked={single || multi ? selected : undefined}
      aria-haspopup={hasSubmenu ? "menu" : undefined}
      aria-disabled={disabled || undefined}
      disabled={disabled}
      display="flex"
      alignItems="center"
      gap="sm"
      minH="36px"
      px="sm"
      py="1xs"
      bg={baseBg}
      color={textColor}
      borderRadius="component.md"
      fontFamily="body"
      fontWeight={500}
      fontSize="md"
      lineHeight="1.2"
      textAlign="left"
      cursor={disabled ? "not-allowed" : "pointer"}
      transition="background-color 100ms"
      outline="none"
      _hover={hoverStyles}
      // keyboard focus → background highlight (not a ring); pressed → stronger.
      _focusVisible={disabled ? undefined : { bg: strongBg }}
      _active={disabled ? undefined : { bg: strongBg }}
      onClick={disabled ? undefined : onClick}
      sx={{ "& svg": { display: "block" } }}
    >
      {multi && <CheckboxVisual value={selected ? "checked" : "unchecked"} size="sm" state={disabled ? "disabled" : "default"} />}
      {leadingIcon && (
        <chakra.span display="inline-flex" flexShrink={0}>
          {leadingIcon}
        </chakra.span>
      )}
      <chakra.span display="flex" flexDirection="column" flex="1" minW="0">
        <chakra.span isTruncated>{children}</chakra.span>
        {description != null && (
          <chakra.span
            isTruncated
            mt="0.5"
            fontSize="xs"
            fontWeight={400}
            color={disabled ? "text.disabled.default" : "text.body.tertiary"}
          >
            {description}
          </chakra.span>
        )}
      </chakra.span>
      {trailing != null && (
        <chakra.span
          flexShrink={0}
          color={disabled ? "text.disabled.default" : "text.body.tertiary"}
        >
          {trailing}
        </chakra.span>
      )}
      {hasSubmenu ? (
        <chakra.span display="inline-flex" flexShrink={0} color={disabled ? "text.disabled.default" : "text.body.tertiary"}>
          <ChevronRightIcon />
        </chakra.span>
      ) : (
        showCheck && (
          <chakra.span display="inline-flex" flexShrink={0} color="text.body.success">
            <CheckIcon />
          </chakra.span>
        )
      )}
    </chakra.button>
  );
}

export default MenuItem;
